"use client";

import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

import type { Currency } from "@/lib/utils/currency";
import { formatCurrencyExact } from "@/lib/utils/currency";
import {
	canAddAnotherPromo,
	recomputeStackedPromos,
	reduceAmountsByPromoDiscount,
	type AppliedPromoCode,
	type PromoCodePackageTier,
	type PromoLineAmounts,
	type ValidatePromoCodeResponse,
} from "@/lib/promo-code";
import { useValidatePromoCodeMutation } from "@/features/promoCodes/promoCodeApiInjection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { errorIntoString } from "@/lib/utils";

type Props = {
	currency: Currency;
	email?: string;
	packageAmount: number;
	licenseAmount?: number;
	hostingAmount?: number;
	planAmount?: number;
	packageTiers?: PromoCodePackageTier[];
	orderAmount: number;
	applied: AppliedPromoCode[];
	onChange: (promos: AppliedPromoCode[]) => void;
	className?: string;
};

export default function PromoCodeRedeemField({
	currency,
	email,
	packageAmount,
	licenseAmount = 0,
	hostingAmount = 0,
	planAmount = 0,
	packageTiers = [],
	orderAmount,
	applied,
	onChange,
	className,
}: Props) {
	const { toast } = useToast();
	const [codeInput, setCodeInput] = useState("");
	const [validatePromoCode, { isLoading }] = useValidatePromoCodeMutation();
	const cartKey = [
		currency,
		packageAmount,
		licenseAmount,
		hostingAmount,
		planAmount,
		orderAmount,
		packageTiers.join(","),
	].join("|");
	const lastCartKey = useRef<string>("");
	const skipNextCartSync = useRef(false);
	const allowAnother = canAddAnotherPromo(applied);

	const handleApply = async () => {
		const code = codeInput.trim().toUpperCase();
		if (!code) {
			toast({
				variant: "destructive",
				title: "Enter a promo code",
			});
			return;
		}
		if (applied.some((p) => p.code === code)) {
			toast({
				variant: "destructive",
				title: "Promo code already applied",
			});
			return;
		}
		if (!allowAnother) {
			toast({
				variant: "destructive",
				title: "Cannot combine promo codes",
				description:
					"Remove the current code first, or use codes that allow combining.",
			});
			return;
		}

		const stacked = recomputeStackedPromos({
			promos: applied,
			packageAmount,
			licenseAmount,
			hostingAmount,
			planAmount,
			orderAmount,
			cartPackageTiers: packageTiers,
		});
		const prior = stacked.ok ? stacked.promos : applied;

		let remaining: PromoLineAmounts = {
			packageAmount,
			licenseAmount,
			hostingAmount,
			planAmount,
		};
		let remainingOrder = orderAmount;
		for (const p of prior) {
			remaining = reduceAmountsByPromoDiscount(remaining, p.applyTo, p.discountAmount);
			remainingOrder = Math.round((remainingOrder - p.discountAmount) * 100) / 100;
		}

		try {
			const result = (await validatePromoCode({
				code,
				currency,
				email,
				packageAmount: remaining.packageAmount,
				licenseAmount: remaining.licenseAmount,
				hostingAmount: remaining.hostingAmount,
				planAmount: remaining.planAmount,
				packageTiers,
				orderAmount: Math.max(0, remainingOrder),
				otherAppliedCodes: prior.map((p) => p.code),
			}).unwrap()) as ValidatePromoCodeResponse;

			skipNextCartSync.current = true;
			lastCartKey.current = cartKey;
			onChange([
				...prior,
				{
					code: result.code,
					label: result.label,
					discountType: result.discountType,
					discountValue: result.discountValue,
					currency: result.currency,
					applyTo: result.applyTo,
					packageTiers: result.packageTiers,
					discountAmount: result.discountAmount,
					stackableWithOtherPromoCodes: result.stackableWithOtherPromoCodes,
					applyDiscountToDueTodayFirst: result.applyDiscountToDueTodayFirst,
				},
			]);
			setCodeInput("");
			toast({ title: "Promo code applied" });
		} catch (err) {
			toast({
				variant: "destructive",
				title: "Could not apply promo code",
				description: errorIntoString(err),
			});
		}
	};

	// Instantly refresh discount when package / payment totals change (no API wait).
	useEffect(() => {
		if (!applied.length) {
			lastCartKey.current = "";
			return;
		}
		if (skipNextCartSync.current) {
			skipNextCartSync.current = false;
			lastCartKey.current = cartKey;
			return;
		}
		if (lastCartKey.current === cartKey) return;
		lastCartKey.current = cartKey;

		const next = recomputeStackedPromos({
			promos: applied,
			packageAmount,
			licenseAmount,
			hostingAmount,
			planAmount,
			orderAmount,
			cartPackageTiers: packageTiers,
		});
		if (!next.ok) {
			onChange([]);
			toast({
				variant: "destructive",
				title: "Promo codes removed",
				description: "These codes no longer apply to your current selection.",
			});
			return;
		}
		const changed =
			next.promos.length !== applied.length ||
			next.promos.some((p, i) => p.discountAmount !== applied[i]?.discountAmount);
		if (changed) onChange(next.promos);
	}, [
		applied,
		cartKey,
		packageAmount,
		licenseAmount,
		hostingAmount,
		planAmount,
		orderAmount,
		packageTiers,
		onChange,
		toast,
	]);

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== "Enter") return;
		e.preventDefault();
		e.stopPropagation();
		void handleApply();
	};

	const removeCode = (code: string) => {
		onChange(applied.filter((p) => p.code !== code));
	};

	return (
		<div className={`flex flex-col gap-1.5 ${className ?? ""}`}>
			{applied.map((promo) => (
				<div
					key={promo.code}
					className="flex min-w-0 items-center justify-between gap-2 text-xs font-medium leading-snug text-secondary"
				>
					<span className="inline-flex min-w-0 items-center gap-1 pl-3">
						<span className="mr-0.5 text-secondary opacity-90" aria-hidden>
							•
						</span>
						<span className="min-w-0 truncate font-semibold text-secondary">
							{promo.code}{" "}
							(
							{promo.discountType === "PERCENTAGE"
								? `${promo.discountValue}%`
								: formatCurrencyExact(promo.discountValue, currency)}
							)
						</span>
						<button
							type="button"
							onClick={() => removeCode(promo.code)}
							className="shrink-0 rounded-full p-0.5 text-grey-foreground/60 transition-colors hover:bg-secondary/15 hover:text-secondary dark:text-muted-foreground"
							aria-label={`Remove promo code ${promo.code}`}
						>
							<X className="h-3.5 w-3.5" />
						</button>
					</span>
					<span className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-secondary">
						-{formatCurrencyExact(promo.discountAmount, currency)}
					</span>
				</div>
			))}
			{allowAnother ? (
				<div className="flex min-w-0 items-center gap-2">
					<Input
						value={codeInput}
						onChange={(ev) => setCodeInput(ev.target.value.toUpperCase())}
						onKeyDown={handleKeyDown}
						placeholder={applied.length ? "Add another promo code" : "Promo code"}
						autoComplete="off"
						className="h-9 min-w-0 flex-1 rounded-full border border-border-visible bg-white px-3.5 text-xs font-semibold uppercase tracking-wide text-secondary shadow-none placeholder:font-medium placeholder:normal-case placeholder:tracking-normal placeholder:text-grey-foreground/55 focus-visible:border-tertiary focus-visible:ring-1 focus-visible:ring-tertiary/35 dark:bg-card dark:placeholder:text-muted-foreground"
					/>
					<Button
						type="button"
						size="sm"
						disabled={isLoading}
						onClick={() => void handleApply()}
						className="h-9 w-auto min-w-[5.5rem] shrink-0 rounded-full border-0 bg-tertiary px-5 text-xs font-semibold text-white shadow-[0_6px_14px_rgba(23,199,184,0.28)] hover:bg-tertiary/85 hover:text-white"
					>
						{isLoading ? "…" : "Apply"}
					</Button>
				</div>
			) : null}
		</div>
	);
}
